// Funzione di mapping per convertire il risultato della RPC in un oggetto Itinerary coerente
function mapRpcItineraryToItinerary(data: any): Itinerary {
  return {
    // Campi obbligatori da Tables<'itineraries'>
    id: data.id ?? data.itinerary_id ?? '',
    travel_request_id: data.travel_request_id ?? '',
    title: data.title ?? data.itinerary_title ?? '',
    status: data.status ?? data.itinerary_status ?? '',
    pdfurl: data.pdfurl ?? null,
    total_days: data.total_days ?? null,
    start_date: data.start_date ?? data.itinerary_start_date ?? '',
    end_date: data.end_date ?? data.itinerary_end_date ?? '',
    created_at: data.created_at ?? data.itinerary_created_at ?? '',
    updated_at: data.updated_at ?? data.itinerary_updated_at ?? '',
    metadata: data.metadata ?? data.itinerary_metadata ?? null,
    contact_email: data.contact_email ?? '',
    contact_first_name: data.contact_first_name ?? null,
    contact_last_name: data.contact_last_name ?? null,
    td_name: data.td_name ?? data.td_first_name ?? null,
    td_surname: data.td_surname ?? data.td_last_name ?? null,
    td_email: data.td_email ?? null,
    pref_name: data.pref_name ?? data.pref_first_name ?? null,
    pref_surname: data.pref_surname ?? data.pref_last_name ?? null,
    pref_email: data.pref_email ?? null,
    // Campi richiesti da Tables<'itineraries'> ma non gestiti dalla RPC: fallback a valori di default
    budget: data.budget ?? 0,
    description: data.description ?? '',
    guests_data: data.guests_data ?? null,
    image: data.image ?? '',
    note: data.note ?? null,
    currency: data.currency ?? null,
    total: data.total ?? null,
  };
}
import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { AuthContext } from '@/lib/auth/models/auth.model';
import { Tables } from '@/integrations/supabase/types';
import { ItineraryData, ItineraryStatus } from '@/lib/builder/models/itinerary.model';
import { ActivityFactory, ItineraryActivity } from '@/lib/builder/models/activity.model';
import { SUPABASE_URL } from '@/components/utils/constants';
import { mediaUrls } from '@/types/media.types';


export type Itinerary = Tables<'itineraries'> & {
  pref_email?: string;
  pref_name?: string;
  pref_surname?: string;
  td_email?: string;
  td_name?: string;
  td_surname?: string;
  contact_email: string;
  contact_first_name?: string;
  contact_last_name?: string;
  travel_request_id: string;
};

interface UseItinerariesOptions {
  statusFilter?: string;
  excludeStatus?: string;
  disableAutoFetch?: boolean; // Nuovo parametro per disabilitare il caricamento automatico
}

// Helper: checks if a URL points to our Supabase storage (internal file)
function isInternalUrl(url: string): boolean {
  if (!url) return false;
  if (SUPABASE_URL && url.startsWith(SUPABASE_URL)) return true;
  return /\/storage\/v1\/object\/(?:public|sign)\/p2b\//.test(url);
}

// Helper function to copy all storage files for an itinerary using edge function
async function copyItineraryStorageFiles(
  oldItineraryId: string,
  newItineraryId: string,
  activityIdMap: Record<string, string>
) {
  try {
    // Fetch activities from OLD itinerary (con media/documentation originali)
    const { data: oldActivities, error: oldError } = await supabase
      .from('itinerary_activities')
      .select('id, media, documentation')
      .eq('itinerary_id', oldItineraryId);

    if (oldError || !oldActivities) {
      return;
    }

    // Prepara array di attività da copiare (solo quelle con file interni a Supabase)
    const activitiesToCopy = oldActivities
      .filter(act => activityIdMap[act.id])
      .map(act => ({
        oldActivityId: act.id,
        newActivityId: activityIdMap[act.id],
        // La edge function copy-itinerary-files lavora sui path storage: le basta
        // l'URL. mediaUrls() appiattisce sia il formato legacy sia i MediaItem.
        media: mediaUrls(act.media).filter(isInternalUrl),
        documentation: (act.documentation || []).filter((doc: { name: string; url: string }) => isInternalUrl(doc.url))
      }))
      .filter(act => act.media.length > 0 || act.documentation.length > 0);

    if (activitiesToCopy.length === 0) {
      return;
    }

    // Chiamata unica alla edge function
    const { data, error } = await supabase.functions.invoke('copy-itinerary-files', {
      body: {
        bucket: 'p2b',
        oldItineraryId,
        newItineraryId,
        activities: activitiesToCopy
      }
    });

    if (error) {
      return;
    }
  } catch (error) {
    // Silent fail
  }
}

export const useItineraries = (options: UseItinerariesOptions = {}) => {
  const [itineraries, setItineraries] = useState<Itinerary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();

  // Usa useRef per memorizzare i valori delle opzioni e evitare loop infiniti
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Recupera l'utente dal contesto di autenticazione
  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error('useItineraries deve essere usato all\'interno di un AuthProvider');
  }

  // Recupera gli itinerari dell'utente corrente dalla materialized view
  const fetchItineraries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Usa RPC function per query ottimizzata con RLS
      const { data, error: fetchError } = await supabase.rpc('get_complete_itineraries', {
        p_itinerary_status: optionsRef.current.statusFilter || null,
        p_travel_request_status: optionsRef.current.excludeStatus ? null : null  // TODO: se serve filtrare travel_request_status
      });
      if (fetchError) {
        setError(t('dashboard.loadItinerariesError'));
        toast({
          title: t('common.error'),
          description: t('dashboard.loadItinerariesError'),
          variant: "destructive",
        });
        return;
      }

      // Mappa i campi dalla materialized view al formato Itinerary
      const mappedData = (data ?? []).map(item => ({
        id: item.itinerary_id,
        travel_request_id: item.travel_request_id,
        title: item.itinerary_title,
        status: item.itinerary_status,
        price: item.price,
        pdfurl: item.pdfurl,
        total_days: item.total_days,
        start_date: item.itinerary_start_date,
        end_date: item.itinerary_end_date,
        created_at: item.itinerary_created_at,
        updated_at: item.itinerary_updated_at,
        metadata: item.itinerary_metadata,
        contact_email: item.contact_email,
        contact_first_name: item.contact_first_name,
        contact_last_name: item.contact_last_name,
        td_name: item.td_first_name,
        td_surname: item.td_last_name,
        td_email: item.td_email,
        pref_name: item.pref_first_name,
        pref_surname: item.pref_last_name,
        pref_email: item.pref_email,
      }));

      setItineraries(mappedData as Itinerary[]);
    } catch (err) {
      setError(t('errors.generic'));
      toast({
        title: t('common.error'),
        description: t('errors.generic'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Carica gli itinerari al mount e quando le opzioni cambiano (solo se non disabilitato)
  useEffect(() => {
    if (!options.disableAutoFetch) {
      fetchItineraries();
    } else {
      // Se il caricamento automatico è disabilitato, imposta loading a false
      setLoading(false);
    }
  }, [options.statusFilter, options.excludeStatus, options.disableAutoFetch, fetchItineraries]);

  // Funzione per aggiornare i dati
  const refreshData = useCallback(async () => {
    await fetchItineraries();
  }, [fetchItineraries]);

  // Funzione per salvare/aggiornare un itinerario
  const saveItinerary = useCallback(async (itineraryData: ItineraryData) => {
    try {
      const { data, error } = await supabase.rpc('save_itinerary', { p_itinerary: itineraryData.getPayload() });
      if (error || !data) {
        toast({
          title: t('common.error'),
          description: t('itinerary.saveUnexpectedError'),
          variant: "destructive",
        });
        return null;
      }
      // Aggiorna la lista solo se il fetch automatico è abilitato.
      // Se disableAutoFetch è true (es. builder) la dashboard ricaricherà al suo mount.
      if (!optionsRef.current.disableAutoFetch) {
        await fetchItineraries();
      }
      return data;
    } catch (err) {
      toast({
        title: t('common.error'),
        description: t('itinerary.saveUnexpectedError'),
        variant: "destructive",
      });
      return null;
    }
  }, []);

  // Funzione per eliminare un itinerario
  const deleteItinerary = useCallback(async (itineraryId: string) => {
    try {
      const { error } = await supabase
        .from('itineraries')
        .delete()
        .eq('id', itineraryId);
      if (error) {
        toast({
          title: t('common.error'),
          description: t('itinerary.deleteError'),
          variant: "destructive",
        });
        return false;
      }
      setItineraries(prev => prev.filter(itinerary => itinerary.id !== itineraryId));
      toast({
        variant: "success",
        title: t("notifications.itineraryDeleted"),
        description: t("notifications.itineraryDeleted"),
      });
      return true;
    } catch (err) {
      toast({
        title: t('common.error'),
        description: t('errors.generic'),
        variant: "destructive",
      });
      return false;
    }
  }, []);

  // Function to fetch existing itinerary by requestId from database
  const fetchItinerary = useCallback(async (requestId: string) => {
    try {
      const { data, error } = await supabase
        .from('itineraries')
        .select('*')
        .eq('travel_request_id', requestId)
        .single();
      if (error && error.code !== 'PGRST116') {
        return null;
      }
      return data;
    } catch (error) {
      return null;
    }
  }, []);

  // Function to fetch itinerary activities
  //
  // Passa dalla RPC e non da una SELECT diretta: lo spaccato prezzi per travel object
  // (cost, include_price, supplier_status) è riservato al travel advisor, e la RPC lo
  // azzera server-side quando a chiamare è il traveler. Nasconderlo solo lato UI
  // lascerebbe i costi nel payload JSON. Ordinamento e forma della riga sono gli stessi
  // della vecchia query, quindi ActivityFactory non cambia.
  const fetchItineraryActivities = useCallback(async (itineraryId: string) => {
    try {
      const { data, error } = await supabase.rpc('get_itinerary_activities' as any, {
        p_itinerary_id: itineraryId,
      });
      if (error) {
        return [];
      }
      return (data || []).map(activity => ActivityFactory.create(activity as ItineraryActivity));
    } catch (error) {
      return [];
    }
  }, []);

  /**
   * Nuova funzione: salva le attività con insert, update, delete separati
   * @param itineraryId
   * @param toInsert array di nuove attività da inserire
   * @param toUpdate array di attività da aggiornare (devono avere id)
   * @param toDelete array di id attività da cancellare
   */
  const saveItineraryActivities = useCallback(
    async (
      itineraryId: string,
      toInsert: ItineraryActivity[],
      toUpdate: ItineraryActivity[],
      toDelete: string[],
      itineraryTotals?: Record<string, number>
    ) => {
      try {
        const payload: Record<string, any> = {
          itineraryId,
          toInsert: toInsert.map(a => {
            const { inOrOutActivity, is_multi_day, ...rest } = a as any;
            return { ...rest, description: rest.description || null };
          }),
          toUpdate: toUpdate.map(a => {
            const { inOrOutActivity, is_multi_day, ...rest } = a as any;
            return { ...rest, description: rest.description || null };
          }),
          toDelete
        };
        if (itineraryTotals) payload.itineraryTotals = itineraryTotals;

        const { data, error } = await supabase.functions.invoke('update-itinerary', { body: payload });
        if (error) {
          toast({ title: t('common.error'), description: t('itinerary_messages.cannotInsertActivities'), variant: 'destructive' });
          return false;
        }

        if (!data || !data.success) {
          // La transazione è fallita - tutte le operazioni sono state annullate
          const errorMessage = data?.error || data?.results?.errors?.[0]?.message || t('itinerary_messages.unexpectedSaveError');

          toast({
            title: t('common.error'),
            description: String(errorMessage),
            variant: 'destructive'
          });
          return false;
        }

        return true;
      } catch (err) {
        toast({ title: t('common.error'), description: t('itinerary_messages.unexpectedSaveError'), variant: 'destructive' });
        return false;
      }
    },
    []);

  // Function to update itinerary status
  const updateItineraryStatus = useCallback(async (itineraryId: string, newStatus: ItineraryStatus, note?: string) => {
    try {
      const updatePayload: Record<string, unknown> = {
        status: newStatus,
        updated_at: new Date().toISOString(),
      };
      if (note !== undefined) {
        updatePayload.note = note || null;
      }
      const { error } = await supabase
        .from('itineraries')
        .update(updatePayload)
        .eq('id', itineraryId);
      if (error) {
        toast({
          title: t('common.error'),
          description: t('itinerary.updateStatusError'),
          variant: "destructive",
        });
        return false;
      }
      setItineraries(prev => prev.map(itinerary =>
        itinerary.id === itineraryId
          ? { ...itinerary, status: newStatus, updated_at: new Date().toISOString() }
          : itinerary
      ));
      return true;
    } catch (err) {
      toast({
        title: t('common.error'),
        description: t('itinerary.updateStatusUnexpectedError'),
        variant: "destructive",
      });
      return false;
    }
  }, []);

  // Function to duplicate an itinerary with all its activities (truly transactional)
  const duplicateItinerary = useCallback(async (itineraryId: string) => {
    try {
      const { data: originalItinerary, error: fetchError } = await supabase
        .from('itineraries')
        .select('title')
        .eq('id', itineraryId)
        .single();
      if (fetchError) {
        toast({
          title: t('common.error'),
          description: t('itinerary.duplicateFetchError'),
          variant: "destructive",
        });
        return null;
      }
      const { data: result, error: rpcError } = await supabase.rpc('duplicate_itinerary', {
        p_itinerary_id: itineraryId,
        p_new_title: `${originalItinerary.title} (Copy)`
      });
      if (rpcError) {
        toast({
          title: t('common.error'),
          description: t('itinerary.duplicateRpcError'),
          variant: "destructive",
        });
        return null;
      }
      if (result && result.new_itinerary) {
        const newItinerary = result.new_itinerary as unknown as Itinerary;
        const activityIdMap = result.activity_id_map as Record<string, string>;
        const oldItineraryId = result.old_itinerary_id as string;
        setItineraries(prev => [newItinerary, ...prev]);
        copyItineraryStorageFiles(oldItineraryId, newItinerary.id, activityIdMap).catch(() => { });
        fetchItineraries();
      }
      return null;
    } catch (err) {
      toast({
        title: t('common.error'),
        description: t('itinerary.duplicateUnexpectedError'),
        variant: "destructive",
      });
      return null;
    }
  }, []);

  return {
    itineraries,
    loading,
    error,
    refreshData,
    saveItinerary,
    deleteItinerary,
    fetchItinerary,
    fetchItineraryActivities,
    saveItineraryActivities,
    duplicateItinerary,
    updateItineraryStatus
  };
};
